const OPENED_DEFINITION =
  'Opened means the user read the document: they opened its tab on the meeting page in the ' +
  'web app, or Wundo opened it for them in the chat when they asked. It counts once per ' +
  'person per meeting per day, whichever way they read it. A superadmin signed in as the ' +
  'user does not count, and neither does Wundo pushing an excerpt (a meeting card, the ' +
  'daily prep digest).\n\n' +
  'Opens are only recorded from the day that tracking shipped; before that the Opened step ' +
  'is zero. That is a measurement change, not a drop.';

const SESSION_DEFINITION =
  'A session is a run of messages from a user where each message is within 30 minutes of the ' +
  'previous one; a longer gap starts a new session.';

/** What every figure on the metrics history page means. */
export const METRIC_INFO = {
  activated:
    'Activated users / all provisioned users.\n\n' +
    'A user counts as activated once they have sent at least one message, opened the app or ' +
    'let the notetaker into one of their meetings; going inactive later does not undo it.\n\n' +
    'The date filter does not apply to this number.',
  sessionsWeek:
    'Sessions in the selected period ÷ users who ever sent a message ÷ days in period × 7.\n\n' +
    'Users who have gone silent count in the denominator, so disengagement lowers the number.\n\n' +
    SESSION_DEFINITION,
  sessionsMonth:
    'Sessions in the selected period ÷ users who ever sent a message ÷ days in period × 30.\n\n' +
    'Users who have gone silent count in the denominator, so disengagement lowers the number.\n\n' +
    SESSION_DEFINITION,
  nps:
    'Standard Net Promoter Score over responses in the selected period:\n' +
    '% promoters (score 9–10) minus % detractors (score 0–6).\n\n' +
    'Passives (7–8) only count in the total. Ranges from −100 to +100.\n\n' +
    'The subtitle shows the plain average of the 0–10 answers.',
  activeUsers:
    'Distinct users who sent at least one message, opened the app or let the notetaker into one ' +
    'of their meetings in the rolling window (7 days for weekly, 30 for monthly), snapshotted ' +
    'daily. Only the owner of the recording counts, not the other attendees.\n\n' +
    'The percentage is relative to all provisioned users. The line under the chart shows the ' +
    'same count against activated users only.\n\n' +
    'Visits to the webapp are only recorded from the day that tracking shipped, so the series ' +
    'steps up on that date. That step is a measurement change, not growth.',
  distribution:
    'Users bucketed by their number of sessions in the selected period. Users without sessions ' +
    'are not shown.\n\n' +
    'The average sessions per user who ever sent a message (silent users included) is ' +
    'printed beside the chart.\n\n' +
    SESSION_DEFINITION,
  nudgeFunnel:
    "Onboarding nudges shown in the chat drawer during a user's first four weeks, snapshotted " +
    'daily over the selected period.\n\n' +
    'Shown and Completed are counted once per user per nudge; Clicked and Dismissed are counted ' +
    'every time, so three dismissals of one nudge count as three.\n\n' +
    'A nudge can complete without ever being shown — the work was already done — so Completed ' +
    'is not strictly a subset of Shown.',
  nudgePerKey:
    'Lifetime totals per nudge, from the nudge state kept per user. The date filter does not ' +
    'apply to these numbers.\n\n' +
    'Shown and Completed are users; Dismissed counts every dismissal. Rate is Completed ÷ ' +
    'Shown.\n\n' +
    'Clicks are recorded as a total only, not per nudge, so there is no click column here.',
  prepFunnel:
    'Generated: preparations Wundo wrote in the period. A preparation that only holds the ' +
    "user's own notes is not counted.\n\n" +
    OPENED_DEFINITION,
  reportFunnel:
    'Created: reports (verslagen) that came into being in the period, whether Wundo wrote them ' +
    'from a recording or the user wrote them from their own notes.\n\n' +
    OPENED_DEFINITION,
  recordings:
    'Recordings that finished with a transcript, by the day of their meeting. Online is the ' +
    'notetaker in a call, the bot or through Google Meet itself; physical is the user recording a meeting in a room with ' +
    'their laptop microphone. Failed recordings are not shown.',
  meetingUsers:
    'Distinct users who, in the rolling 7-day window, opened a preparation or a report, or ' +
    'had a meeting whose recording finished, snapshotted daily. Only the owner of a recording counts.\n\n' +
    'The percentage is relative to all provisioned users.\n\n' +
    'Opens are only recorded from the day that tracking shipped, so before that date the ' +
    'series counts recordings only.',
} as const;
